import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  ShoppingCart, 
  ShoppingBag, 
  Database, 
  Trash2, 
  ArrowRightLeft, 
  Edit,
  ArrowDown,
  ArrowUp
} from "lucide-react";

type MovementType = "Sales" | "Purchase" | "Opening Balance" | "Write-off" | "Manual Adjustment";

interface MovementTypeSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectType: (type: MovementType) => void;
}

export function MovementTypeSelectionDialog({
  open,
  onOpenChange,
  onSelectType,
}: MovementTypeSelectionDialogProps) {
  const movementTypes = [
    {
      icon: <ArrowDown className="h-5 w-5 text-blue-600" />,
      title: "Sales",
      description: "Record stock sold to customers",
      type: "Sales" as MovementType,
      bgColor: "bg-blue-50",
      borderColor: "border-blue-300",
      hoverBg: "hover:bg-blue-50",
      hoverBorder: "hover:border-blue-300",
    },
    {
      icon: <ArrowUp className="h-5 w-5 text-green-600" />,
      title: "Purchase",
      description: "Record stock received from suppliers",
      type: "Purchase" as MovementType,
      bgColor: "bg-green-50",
      borderColor: "border-green-300",
      hoverBg: "hover:bg-green-50",
      hoverBorder: "hover:border-green-300",
    },
    {
      icon: <Database className="h-5 w-5 text-purple-600" />,
      title: "Opening Balance",
      description: "Set initial stock quantities",
      type: "Opening Balance" as MovementType,
      bgColor: "bg-purple-50",
      borderColor: "border-purple-300",
      hoverBg: "hover:bg-purple-50",
      hoverBorder: "hover:border-purple-300",
    },
    {
      icon: <Trash2 className="h-5 w-5 text-red-600" />,
      title: "Write-off",
      description: "Remove damaged or obsolete stock",
      type: "Write-off" as MovementType,
      bgColor: "bg-red-50",
      borderColor: "border-red-300",
      hoverBg: "hover:bg-red-50",
      hoverBorder: "hover:border-red-300",
    },
    {
      icon: <Edit className="h-5 w-5 text-gray-600" />,
      title: "Manual Adjustment",
      description: "Correct stock quantities",
      type: "Manual Adjustment" as MovementType,
      bgColor: "bg-gray-50",
      borderColor: "border-gray-300",
      hoverBg: "hover:bg-gray-50",
      hoverBorder: "hover:border-gray-300",
    },
  ];

  const handleSelectType = (type: MovementType) => {
    onSelectType(type);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Stock Movement</DialogTitle>
          <DialogDescription>
            Select the type of stock movement you want to create
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {movementTypes.map((movement) => (
            <Button
              key={movement.type}
              variant="outline"
              className={`h-auto py-4 px-4 flex items-start gap-3 ${movement.hoverBg} ${movement.hoverBorder} w-full justify-start`}
              onClick={() => handleSelectType(movement.type)}
            >
              <div className={`rounded-full ${movement.bgColor} p-2`}>
                {movement.icon}
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">{movement.title}</div>
                <div className="text-sm text-muted-foreground">{movement.description}</div>
              </div>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
